import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge, Button, Group, Loader, NumberInput, Paper, SimpleGrid, Stack, Switch, Table, Text, Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconGift } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import {
  useLoyaltySettings, useUpdateLoyaltySettings, useLoyaltyLedger,
} from './loyaltyApi';

const fmtDate = (iso: string) =>
  new Intl.DateTimeFormat('ar-PS', { dateStyle: 'medium', timeStyle: 'short', numberingSystem: 'latn' }).format(new Date(iso));

export function LoyaltyPage() {
  const { t } = useTranslation();
  const settings = useLoyaltySettings();
  const update = useUpdateLoyaltySettings();
  const ledger = useLoyaltyLedger();

  const form = useForm({
    initialValues: { earnPointsPerCurrency: 1, redeemValuePerPoint: 0, minRedeemPoints: 0, isActive: true },
  });

  useEffect(() => {
    if (settings.data) form.setValues(settings.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data]);

  const save = form.onSubmit(async (v) => {
    try {
      await update.mutateAsync(v);
      notifications.show({ color: 'green', title: t('common.toasts.updated'), message: t('loyalty.settings.saved') });
    } catch (err) {
      notifications.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  });

  const typeBadge = (t0: number) => {
    if (t0 === 0) return <Badge color="green" variant="light" radius="sm">{t('loyalty.types.earn')}</Badge>;
    if (t0 === 1) return <Badge color="red" variant="light" radius="sm">{t('loyalty.types.redeem')}</Badge>;
    return <Badge color="blue" variant="light" radius="sm">{t('loyalty.types.adjust')}</Badge>;
  };

  const rows = ledger.data ?? [];

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.sections.organization')} title={t('loyalty.title')} subtitle={t('loyalty.subtitle')} />

      <Paper p="lg">
        <form onSubmit={save}>
          <Stack gap="md">
            <Title order={5} c="var(--wheels-ink-strong)">{t('loyalty.settings.title')}</Title>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <NumberInput label={t('loyalty.settings.earnRate')} description={t('loyalty.settings.earnRateHelp')}
                min={0} step={0.5} decimalScale={2} {...form.getInputProps('earnPointsPerCurrency')} />
              <NumberInput label={t('loyalty.settings.redeemValue')} description={t('loyalty.settings.redeemValueHelp')}
                min={0} step={0.05} decimalScale={4} {...form.getInputProps('redeemValuePerPoint')} />
              <NumberInput label={t('loyalty.settings.minRedeem')} description={t('loyalty.settings.minRedeemHelp')}
                min={0} step={1} {...form.getInputProps('minRedeemPoints')} />
            </SimpleGrid>
            <Switch label={t('loyalty.settings.active')} description={t('loyalty.settings.activeHelp')}
              checked={form.values.isActive} onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)} />
            <Group justify="flex-end">
              <Button type="submit" loading={update.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Paper>

      <div>
        <Title order={5} c="var(--wheels-ink-strong)" mb="sm">{t('loyalty.ledger.title')}</Title>
        <Paper p={rows.length ? 0 : 'lg'}>
          {ledger.isLoading ? (
            <Stack align="center" py={48}><Loader size="sm" /></Stack>
          ) : rows.length === 0 ? (
            <EmptyState icon={<IconGift size={22} stroke={1.6} />} title={t('loyalty.ledger.empty')} description={t('loyalty.ledger.emptyBody')} />
          ) : (
            <Table verticalSpacing="sm" highlightOnHover striped="even">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('loyalty.ledger.when')}</Table.Th>
                  <Table.Th>{t('loyalty.ledger.customer')}</Table.Th>
                  <Table.Th>{t('loyalty.ledger.type')}</Table.Th>
                  <Table.Th style={{ textAlign: 'end' }}>{t('loyalty.ledger.points')}</Table.Th>
                  <Table.Th style={{ textAlign: 'end' }}>{t('loyalty.ledger.balance')}</Table.Th>
                  <Table.Th>{t('loyalty.ledger.note')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((e) => (
                  <Table.Tr key={e.loyaltyPointsEntryId}>
                    <Table.Td className="font-numeric" c="var(--wheels-ink-muted)">{fmtDate(e.createdAt)}</Table.Td>
                    <Table.Td>
                      <Text fw={500} c="var(--wheels-ink-strong)">{e.customerName}</Text>
                      <Text size="xs" c="var(--wheels-ink-muted)" className="font-numeric">{e.customerPhone}</Text>
                    </Table.Td>
                    <Table.Td>{typeBadge(e.entryType)}</Table.Td>
                    <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 600, color: e.points < 0 ? 'var(--wheels-red, #B5191D)' : 'var(--mantine-color-green-7)' }}>
                      {e.points > 0 ? '+' : ''}{e.points}
                    </Table.Td>
                    <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{e.balanceAfter}</Table.Td>
                    <Table.Td c="var(--wheels-ink-muted)">{e.note ?? '—'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </div>
    </Stack>
  );
}
